import React, { useEffect, useImperativeHandle, useRef } from "react";
import { IPersonalizedSuggestionProps } from "../../types/suggestions";
import {
    IMappedContent,
    IMappedPersonalizedProducts,
    IMappedStoredUserQuery,
    IMappedTopSearchQuery,
    IPromotion
} from "../../types/suggestion-items";
import { IBrick } from "../../types/suggestion-response";
import { ISearchInfo } from "../../types/search-info";
import { KrakenRecommendationViewedEvent } from "../../types/kraken";
import { TelemetryContentEnum } from "../../types/telemetry";

import { useL10nContext } from "../../util/contexts";
import {
    useProductRenderAmount,
    cropProductLists
} from "../../util/suggestions/product-render-amount";
import {
    mapViewToKrakenFragmentName,
    sendRecommendationEventToKraken
} from "../../util/events/kraken";
import ProductTiles from "../suggestion-items/product-tiles/product-tiles";
import StoredSearchQuery from "../suggestion-items/stored-search-query";
import TopSearchQuery from "../suggestion-items/top-search-query";
import mapProductFinderL10nToPromotion from "../suggestion-items/promotion/finder-l10n-to-promotion";
import ContentLink from "../suggestion-items/content-link";
import Promotion from "../suggestion-items/promotion/promotion";

const PersonalizedSuggestionList = (props: IPersonalizedSuggestionProps) => {
    const navigationRefs = useRef<(HTMLElement | null)[]>([]);

    const {
        decodeProductFinderL10n,
        articlesHeadline,
        contentHeadline,
        seeAllResults,
        topSearchQueriesHeadline,
        lastSearchQueriesHeadline
    } = useL10nContext();
    const pfL10n = decodeProductFinderL10n();
    const promotions: Record<string, IPromotion> = mapProductFinderL10nToPromotion(pfL10n);

    let listLength = 0;
    const mappedFeaturePromotion =
        props.isMobileShop || !props.featurePromotion?.trim()
            ? null
            : promotions[props.featurePromotion.toLowerCase()] || null;
    const showPromotion = mappedFeaturePromotion !== null;
    listLength = showPromotion ? 1 : 0;

    const visibleProducts = useProductRenderAmount(
        props.view,
        props.deviceType,
        "list",
        false,
        props.isPersonalizedData
    );

    // Map suggestion items in the order in which they will be rendered.
    const mappedTopSearchQueries = mapTopSearchQueries(listLength);
    listLength += mappedTopSearchQueries.length;

    const mappedProducts = mapProducts(listLength);
    const addHeadline = mappedProducts.headline !== undefined ? 1 : 0;
    const addMoreButton = mappedProducts.showMoreButton !== undefined ? 1 : 0;
    const productsLength =
        addHeadline +
        mappedProducts.articles.length +
        mappedProducts.flexibleSets.length +
        addMoreButton;
    const showProducts = productsLength > 0;
    listLength += productsLength;

    const mappedStoredQueries = mapStoredQueries(listLength);
    listLength += mappedStoredQueries.length;

    const mappedContentLinks = mapContentLinks(listLength);
    listLength += mappedContentLinks.length;

    const searchInfo: ISearchInfo = {
        pageViewLogDataId: props.pageViewLogDataId,
        culture: props.culture,
        portal: props.portal,
        searchTerm: props.isPersonalizedData ? "" : props.searchTerm,
        trackingEndpoint: props.trackingEndpoint
    };

    if (props.isPersonalizedData && !props.recoViewedEventSent) {
        const recoViewedEventData: KrakenRecommendationViewedEvent = {
            fragmentName: mapViewToKrakenFragmentName(props.view),
            products: mappedProducts.articles.map((article, i) => {
                return {
                    articleName: article.title,
                    masterArticleNo: article.masterArticleNo,
                    salesArticleNo: article.salesArticleNo,
                    position: i + 1
                };
            })
        };
        sendRecommendationEventToKraken(recoViewedEventData);
    }

    useEffect(() => {
        navigationRefs.current = navigationRefs.current.filter(Boolean);
    }, [props, mappedProducts]);

    const setRef = (key: number, element: HTMLElement | null) => {
        navigationRefs.current[key] = element;
    };

    useImperativeHandle(props.navigationRef, () => ({
        getNavigationElements: () => navigationRefs.current,
        hasNavigationElements: () => navigationRefs.current.some((el) => el !== null)
    }));

    function mapProducts(length: number): IMappedPersonalizedProducts {
        let croppedLength = props.articles.length + props.flexibleSets.length;
        if (croppedLength === 0) return { articles: [], flexibleSets: [], clickableItemsCount: 0 };

        // crop lists according to device type/size
        const croppedList = cropProductLists(
            [...props.articles],
            [...props.flexibleSets],
            visibleProducts
        );
        croppedLength = croppedList.articles.length + croppedList.sets.length;

        let navIndex = length;
        const result: IMappedPersonalizedProducts = {
            headline: {
                title: articlesHeadline,
                type: TelemetryContentEnum.Articles,
                navIndex: navIndex++
            },
            articles: [
                ...croppedList.articles.map((article) => {
                    return {
                        isHeadline: false,
                        title: article.title,
                        image: article.image,
                        price: article.price,
                        target: article.url,
                        shortLink: article.shortLink,
                        type: TelemetryContentEnum.Articles,
                        navIndex: navIndex++,
                        availableColorsCount: article.availableColorsCount,
                        availableModelsCount: article.availableModelsCount,
                        masterArticleNo: article.masterArticleNumber,
                        salesArticleNo: article.salesArticleNumber
                    };
                })
            ],
            flexibleSets: [
                ...croppedList.sets.map((set) => {
                    return {
                        articleCount: set.articleCount,
                        brickItems: [
                            ...set.brickItems.map((b) => {
                                return {
                                    articleName: b.articleName,
                                    articleImage: b.articleImage
                                } as IBrick;
                            })
                        ],
                        flexibleSetImage: set.flexibleSetImage,
                        isHeadline: false,
                        navIndex: navIndex++,
                        price: set.price,
                        shortLink: set.shortLink,
                        title: set.title,
                        type: TelemetryContentEnum.FlexibleSet,
                        url: set.url
                    };
                })
            ],
            clickableItemsCount: croppedLength
        };

        if (!props.isPersonalizedData && croppedLength >= 2) {
            result.showMoreButton = {
                isHeadline: false,
                title: seeAllResults,
                image: "",
                target: `${props.searchTarget}?query=${encodeURIComponent(props.searchTerm)}`,
                shortLink: "",
                type: TelemetryContentEnum.ShowAllResults,
                navIndex: navIndex++,
                availableColorsCount: 0,
                availableModelsCount: 0,
                masterArticleNo: 0,
                salesArticleNo: ""
            };
            result.clickableItemsCount++;
        }

        return result;
    }

    function mapTopSearchQueries(length: number): IMappedTopSearchQuery[] {
        if (!props.topSearchQueries.length) return [];

        let navIndex = length;
        const result: IMappedTopSearchQuery[] = [
            {
                value: topSearchQueriesHeadline,
                isHeadline: true,
                target: "",
                type: TelemetryContentEnum.TopSearchQuery,
                navIndex: navIndex++
            },
            ...props.topSearchQueries.map((query, index) => ({
                value: query,
                isHeadline: false,
                target: `${props.searchTarget}?query=${encodeURIComponent(query)}`,
                type: TelemetryContentEnum.TopSearchQuery,
                navIndex: navIndex++,
                anchorIndex: index
            }))
        ];

        return result;
    }

    function mapStoredQueries(length: number): IMappedStoredUserQuery[] {
        if (!props.storedQueries.length) return [];

        let navIndex = length;
        const result: IMappedStoredUserQuery[] = [
            {
                value: lastSearchQueriesHeadline,
                isHeadline: true,
                target: "",
                navIndex: navIndex++
            },
            ...props.storedQueries.map((query, index) => ({
                value: query.name,
                isHeadline: false,
                target: query.target,
                navIndex: navIndex++,
                anchorIndex: index
            }))
        ];

        return result;
    }

    function mapContentLinks(length: number): IMappedContent[] {
        if (!props.contents.length) return [];

        let navIndex = length;
        const result: IMappedContent[] = [
            {
                isHeadline: true,
                title: contentHeadline,
                target: "",
                shortLink: "",
                type: TelemetryContentEnum.Content,
                navIndex: navIndex++
            },
            ...props.contents.map((content, index) => ({
                isHeadline: false,
                title: content.title,
                target: content.url,
                shortLink: content.shortLink,
                type: TelemetryContentEnum.Content,
                navIndex: navIndex++,
                anchorIndex: index
            }))
        ];

        return result;
    }

    return (
        <div
            className={`suggestions-container merged-column list${props.isPersonalizedData ? " personalized" : ""}`}
        >
            <ul className="suggestions-list-container">
                {showPromotion && (
                    <Promotion
                        key={mappedFeaturePromotion.navIndex}
                        setRef={(el) => setRef(mappedFeaturePromotion.navIndex, el)}
                        promotion={mappedFeaturePromotion}
                        style="stretchy"
                        searchInfo={searchInfo}
                    />
                )}

                {mappedTopSearchQueries.map((query, index) => (
                    <TopSearchQuery
                        key={index}
                        setRef={(el) => setRef(query.navIndex, el)}
                        topSearchQuery={query}
                        searchInfo={searchInfo}
                        isPersonalized={props.isPersonalizedData}
                    />
                ))}

                {showProducts && (
                    <ProductTiles
                        products={mappedProducts}
                        setRef={setRef}
                        customerType={props.customerType}
                        hasLeftListItems={false}
                        isMobileShop={props.isMobileShop}
                        priceMandantSettings={props.priceMandantSettings}
                        priceMandantSettingsV2={props.priceMandantSettingsV2}
                        searchInfo={searchInfo}
                        isPersonalized={props.isPersonalizedData}
                    />
                )}

                {mappedStoredQueries.map((query, index) => (
                    <StoredSearchQuery
                        key={index}
                        setRef={(el) => setRef(query.navIndex, el)}
                        storedQuery={query}
                        searchInfo={searchInfo}
                    />
                ))}

                {mappedContentLinks.map((contentLink, index) => (
                    <ContentLink
                        key={index}
                        setRef={(el) => setRef(contentLink.navIndex, el)}
                        contentLink={contentLink}
                        searchInfo={searchInfo}
                    />
                ))}
            </ul>
        </div>
    );
};

export default PersonalizedSuggestionList;
